// FireworksShow.jsx
// 🎆 이스터에그 (v7.371): "정보 없는 빈 공간"을 10번 연속 클릭하면 발동하는 불꽃놀이 쇼.
//   트리거 판정은 App.js 쪽 document click 핸들러가 한다 (여긴 순수 연출만).
//   v7.372: 지속시간 7초 → 15초, 폭죽 소리 추가(Web Audio 실시간 합성).
//
//   ⚠ 최상단을 감싸는 기존 <ClickSpark> 와 [다른] 컴포넌트다.
//     ・ ClickSpark  = reactbits, "클릭할 때마다" 그 지점에서 스파크가 퍼지는 상시 효과.
//     ・ FireworksShow = 발동 후 [자동으로] 로켓을 쏘아 올려 터뜨리는 캔버스 애니메이션.
//     둘을 합치지 않은 이유: ClickSpark 는 사용자 클릭에 반응하는 구조라
//     "가만히 두면 알아서 터지는 불꽃놀이"를 만들 수 없다. 스파크의 [생김새]만 맞췄다
//     (가는 선이 방사형으로 뻗는 모양 — ClickSpark 와 시각적으로 이어진다).
//
//   ⚠ 외부 의존성 0 / 에셋 파일 0.
//     ・ 그림: 순수 canvas 2d (ogl 쓰는 Lightfall/Particles 와 달리 package.json 무영향).
//     ・ 소리: Web Audio API 로 [실시간 합성] — mp3/wav 파일을 안 쓴다.
//       public/ 은 NAS 에만 있어 오디오 파일을 넣을 수 없고, 넣더라도 배포마다 누락 위험.
//       그래서 화이트노이즈 + 오실레이터로 "붐 + 크래클"을 그 자리에서 만든다.
//   ⚠ open=false 면 null 반환 → 마운트/언마운트로 RAF 루프와 AudioContext 를 통째로 정리.
//     상시 렌더가 아니라 발동 순간에만 존재하므로 평소 성능/소리 영향 0.
//   ⚠ 자동재생 정책: AudioContext 는 [사용자 제스처] 이후에만 소리가 난다.
//     이 쇼는 '10번째 클릭' 직후 마운트되므로 transient activation 이 살아 있어 재생된다.
import React, { useRef, useEffect } from 'react';

// 다크/라이트 어디서 터져도 선명한 고채도 팔레트 (Tailwind 400 계열).
const PALETTE = ['#60A5FA', '#34D399', '#FBBF24', '#F87171', '#A78BFA', '#F472B6', '#22D3EE', '#FB923C'];

export default function FireworksShow({ open, onClose, duration = 15000 }) {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const rafRef = useRef(0);
  // onClose 를 ref 로 잡는다 — 인라인 콜백이 매 렌더 새로 만들어져도
  //   effect 를 재실행(애니메이션 재시작)시키지 않게 하려는 것. deps 는 [open, duration] 만.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0;
    let H = 0;

    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    // ─────────────────── 🔊 오디오: 실시간 합성 ───────────────────
    //   audioCtx  : 하나만 만들고 끝날 때 close().
    //   master    : 전체 볼륨(사무실 배려로 과하지 않게) → 컴프레서 → 스피커.
    //   comp      : 폭발이 겹쳐도 클리핑(찢어짐) 안 나게 눌러 준다.
    //   noiseBuf  : 화이트노이즈 1초 — '크랙/크래클'의 재료. 매 폭발이 재사용.
    let audioCtx = null;
    let master = null;
    let noiseBuf = null;
    let supportsPan = false;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        audioCtx = new AC();
        if (audioCtx.state === 'suspended' && audioCtx.resume) audioCtx.resume();
        master = audioCtx.createGain();
        master.gain.value = 0.6;
        const comp = audioCtx.createDynamicsCompressor();
        comp.threshold.value = -18;
        comp.ratio.value = 12;
        master.connect(comp);
        comp.connect(audioCtx.destination);
        const len = Math.floor(audioCtx.sampleRate * 1.0);
        noiseBuf = audioCtx.createBuffer(1, len, audioCtx.sampleRate);
        const d = noiseBuf.getChannelData(0);
        for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
        supportsPan = typeof audioCtx.createStereoPanner === 'function';
      }
    } catch (e) {
      audioCtx = null; // 소리는 있으면 좋고 없어도 그림은 돈다
    }

    // 화면 x 위치를 좌우 패닝(-1~1)으로 — 왼쪽에서 터지면 왼쪽에서 소리.
    const panFor = (x) => {
      if (!supportsPan) return master;
      const pan = audioCtx.createStereoPanner();
      pan.pan.value = Math.max(-1, Math.min(1, (x / (W || 1)) * 2 - 1));
      pan.connect(master);
      return pan;
    };

    // 폭발음: ① 저역 붐(몸통) ② 밴드패스 노이즈 크랙(파열) ③ 하이패스 노이즈 크래클(잔향)
    const playBurst = (x) => {
      if (!audioCtx || !master) return;
      const t = audioCtx.currentTime;
      const out = panFor(x);
      const v = 0.8 + Math.random() * 0.4;

      const boom = audioCtx.createOscillator();
      const bg = audioCtx.createGain();
      boom.type = 'sine';
      boom.frequency.setValueAtTime(150 + Math.random() * 60, t);
      boom.frequency.exponentialRampToValueAtTime(45, t + 0.18);
      bg.gain.setValueAtTime(0.0001, t);
      bg.gain.exponentialRampToValueAtTime(0.5 * v, t + 0.012);
      bg.gain.exponentialRampToValueAtTime(0.0001, t + 0.34);
      boom.connect(bg).connect(out);
      boom.start(t);
      boom.stop(t + 0.4);

      if (noiseBuf) {
        const n = audioCtx.createBufferSource();
        n.buffer = noiseBuf;
        const bp = audioCtx.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.value = 900 + Math.random() * 500;
        bp.Q.value = 0.7;
        const ng = audioCtx.createGain();
        ng.gain.setValueAtTime(0.0001, t);
        ng.gain.exponentialRampToValueAtTime(0.6 * v, t + 0.006);
        ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
        n.connect(bp).connect(ng).connect(out);
        n.start(t);
        n.stop(t + 0.5);

        const c = audioCtx.createBufferSource();
        c.buffer = noiseBuf;
        c.playbackRate.value = 0.8 + Math.random() * 0.5;
        const hp = audioCtx.createBiquadFilter();
        hp.type = 'highpass';
        hp.frequency.value = 3500;
        const cg = audioCtx.createGain();
        cg.gain.setValueAtTime(0.0001, t + 0.04);
        cg.gain.exponentialRampToValueAtTime(0.22 * v, t + 0.09);
        cg.gain.exponentialRampToValueAtTime(0.0001, t + 0.7);
        c.connect(hp).connect(cg).connect(out);
        c.start(t + 0.03);
        c.stop(t + 0.75);
      }
    };

    // 발사음: 올라가는 휘파람. 아주 작게(계속 나므로 배경처럼만).
    const playLaunch = (x) => {
      if (!audioCtx || !master || !noiseBuf) return;
      const t = audioCtx.currentTime;
      const out = panFor(x);
      const n = audioCtx.createBufferSource();
      n.buffer = noiseBuf;
      n.playbackRate.value = 1.2;
      const bp = audioCtx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.Q.value = 1.4;
      bp.frequency.setValueAtTime(500, t);
      bp.frequency.exponentialRampToValueAtTime(1700, t + 0.5);
      const g = audioCtx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.08, t + 0.05);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.55);
      n.connect(bp).connect(g).connect(out);
      n.start(t);
      n.stop(t + 0.6);
    };
    // ──────────────────────────────────────────────────────────────

    const rockets = []; // 상승 중
    const sparks = [];  // 터진 파편
    const G = 0.045;    // 중력
    const rand = (a, b) => a + Math.random() * (b - a);

    const launch = () => {
      const x = rand(W * 0.15, W * 0.85);
      playLaunch(x);
      rockets.push({
        x,
        y: H + 8,
        vx: rand(-0.6, 0.6),
        vy: -rand(7.0, 9.4),
        targetY: rand(H * 0.12, H * 0.46),
        color: PALETTE[(Math.random() * PALETTE.length) | 0],
      });
    };

    const explode = (x, y, color) => {
      playBurst(x);
      const n = 26 + ((Math.random() * 22) | 0);
      const baseSpeed = rand(2.4, 4.2);
      for (let i = 0; i < n; i++) {
        const ang = (Math.PI * 2 * i) / n + rand(-0.09, 0.09);
        const sp = baseSpeed * rand(0.55, 1.15);
        sparks.push({
          x, y,
          vx: Math.cos(ang) * sp,
          vy: Math.sin(ang) * sp,
          life: 1,
          decay: rand(0.012, 0.024),
          color,
          len: rand(6, 12),
        });
      }
    };

    // 시작하자마자 두 발 — "10번째 클릭 순간 뭔가 터졌다"는 즉각 피드백.
    launch();
    launch();

    const t0 = performance.now();
    let launchTimer = 0;
    let nextGap = rand(280, 520);
    let closing = false;

    const frame = (now) => {
      const elapsed = now - t0;

      // 화면을 완전히 지우지 않고 살짝만 덮어 꼬리(트레일)를 남긴다.
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = 'rgba(6,8,20,0.28)';
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'lighter'; // 겹치는 불빛이 밝게 합쳐지도록

      // ── 로켓 ──
      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.x += r.vx;
        r.y += r.vy;
        r.vy += G * 1.1;
        ctx.strokeStyle = r.color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(r.x, r.y);
        ctx.lineTo(r.x - r.vx * 2, r.y - r.vy * 2);
        ctx.stroke();
        // 정점(상승세가 꺾이거나 목표 높이 도달)에서 터진다.
        if (r.vy >= 0 || r.y <= r.targetY) {
          explode(r.x, r.y, r.color);
          rockets.splice(i, 1);
        }
      }

      // ── 스파크 ──
      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i];
        s.vx *= 0.985;
        s.vy = s.vy * 0.985 + G;
        s.x += s.vx;
        s.y += s.vy;
        s.life -= s.decay;
        if (s.life <= 0) {
          sparks.splice(i, 1);
          continue;
        }
        ctx.globalAlpha = Math.max(s.life, 0);
        ctx.strokeStyle = s.color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(s.x - s.vx * s.len * 0.4, s.y - s.vy * s.len * 0.4);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;

      // 종료 페이즈 전까지는 주기적으로 새 로켓 발사.
      if (!closing) {
        launchTimer += 16;
        if (launchTimer > nextGap) {
          launch();
          launchTimer = 0;
          nextGap = rand(280, 520);
        }
      }

      // duration 지나면 [새 발사만] 멈추고, 남은 불꽃이 다 스러지면 닫는다.
      if (elapsed > duration && !closing) {
        closing = true;
        if (overlayRef.current) overlayRef.current.style.opacity = '0';
      }
      if (closing && rockets.length === 0 && sparks.length === 0) {
        cancelAnimationFrame(rafRef.current);
        if (onCloseRef.current) onCloseRef.current();
        return;
      }
      rafRef.current = requestAnimationFrame(frame);
    };
    rafRef.current = requestAnimationFrame(frame);

    // 페이드 인
    const fadeId = requestAnimationFrame(() => {
      if (overlayRef.current) overlayRef.current.style.opacity = '1';
    });

    const onKey = (e) => {
      if (e.key === 'Escape' && onCloseRef.current) onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      cancelAnimationFrame(rafRef.current);
      cancelAnimationFrame(fadeId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('keydown', onKey);
      try { if (audioCtx && audioCtx.close) audioCtx.close(); } catch (e) { /* noop */ }
    };
  }, [open, duration]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      onClick={() => { if (onCloseRef.current) onCloseRef.current(); }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000000, // 다른 모든 모달(≤100000) 위. 순수 연출이라 최상단이 맞다.
        background: 'rgba(6,8,20,0.82)',
        opacity: 0,
        transition: 'opacity 420ms ease',
        cursor: 'pointer',
        overflow: 'hidden',
      }}
    >
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, display: 'block' }} />
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 34,
          textAlign: 'center',
          color: 'rgba(255,255,255,0.55)',
          fontSize: 13,
          letterSpacing: '0.04em',
          pointerEvents: 'none',
          fontFamily: 'inherit',
        }}
      >
        🎆 아무 곳이나 클릭하면 닫힙니다
      </div>
    </div>
  );
}
